/*
 * What the drive takes from the running client (`FrontendModule.bind`, `core/modules/bind.ts`): one
 * watch that keeps the slice's spaces right when the reader's groups change. A space belongs to groups,
 * and whom a group holds is core's (`account.group.changed`), so the drive hears no event of its own
 * when a person joins or leaves a group of a shared drive: it reads its list of spaces again (S-06
 * Appendix D).
 *
 * It reads again only once a page has read the list (`spacesRead`): every build binds every module,
 * also on an instance that does not run the drive, and a reader who never opened the drive has no list
 * to keep right. The HTTP code arrives as a chunk of its own; this file is what the entry carries.
 */

import type { ModuleBindings } from "../../core/modules/registry.ts"
import type { ClientStore } from "../../core/store/store.ts"
import { driveSlice, withDriveSlice, withSpaceList } from "./reducer.ts"

/**
 * Reads the reader's spaces and writes them into the slice: what the sidebar of drives, the home's
 * cards and a group change call. A failure leaves the slice as it was; the page that asked shows its
 * own error.
 */
export async function refreshSpaces(client: ClientStore): Promise<void> {
	const { listSpaces } = await import("./api.ts")
	const list = await listSpaces()
	client.update((model) => withDriveSlice(model, withSpaceList(driveSlice(model), list)))
}

/** Binds the module: the group watch. The answer unbinds it. */
export function bindDriveModule(b: ModuleBindings): () => void {
	return b.client.onEvent((event, result) => {
		if (event.type !== "account.group.changed" || result !== "applied") return
		if (!driveSlice(b.client.model).spacesRead) return
		refreshSpaces(b.client).catch(() => {
			// The list stays as it was; the next group change or page read tries again.
		})
	})
}
